import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../../hooks/useApi';
import { listCoachSeries, listTables, listUsers, type CoachSeriesActiveFilter } from '../../lib/api';
import { DataTable } from '../../components/DataTable';
import { ManageCoachRole } from './ManageCoachRole';
import { PS, T } from '../../lib/theme';
import { formatClock } from '../../lib/time';
import { fullName } from '../../lib/format';
import { seriesKindLabel, weekdayShortLabel } from '../../lib/coach-series';
import type { AdminLookupUser, CoachSeries, Paged, TableEmbed } from '../../lib/types';
import { compareDateDescending, orderByDateDescending } from '../../lib/admin-list-order';

const actionBase: CSSProperties = {
  padding: '8px 16px',
  borderRadius: 3,
  minHeight: 40,
  display: 'inline-flex',
  alignItems: 'center',
  ...T.buttonLabel,
  textTransform: 'uppercase',
  textDecoration: 'none',
};

/** Creating a recurring series stays the primary action on this page. */
const primaryAction: CSSProperties = {
  ...actionBase,
  background: PS.red,
  color: PS.white,
};

const secondaryAction: CSSProperties = {
  ...actionBase,
  background: PS.white,
  color: PS.nearBlack,
  border: `1px solid ${PS.border}`,
};

export function CoachesPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<CoachSeriesActiveFilter>('true');

  const seriesLoader = useCallback(
    (signal: AbortSignal) => listCoachSeries({ active: activeFilter }, signal),
    [activeFilter],
  );
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  // Coaches are a small list (typically < 20); fetch up to 200 users and
  // resolve names client-side — same approach as the new-series form.
  const usersLoader = useCallback((signal: AbortSignal) => listUsers({ limit: 200 }, signal), []);

  const series = useApi<CoachSeries[]>(seriesLoader);
  const tables = useApi<TableEmbed[]>(tablesLoader);
  const users = useApi<Paged<AdminLookupUser>>(usersLoader);
  const orderedSeries = useMemo(
    () => orderByDateDescending(series.data ?? [], (item) => ({ date: item.starts_on, id: item.id })),
    [series.data],
  );

  const tableNameById = useMemo(() => {
    const m = new Map<number, string>();
    (tables.data ?? []).forEach((t) => m.set(t.id, t.name));
    return m;
  }, [tables.data]);

  const coachNameById = useMemo(() => {
    const m = new Map<string, string>();
    (users.data?.items ?? []).forEach((u) =>
      m.set(u.id, u.phone ? `${fullName(u)} · ${u.phone}` : fullName(u)),
    );
    return m;
  }, [users.data]);

  const columns = useMemo<ColumnDef<CoachSeries, unknown>[]>(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        cell: (info) => `#${info.getValue<number>()}`,
      },
      {
        header: 'Coach',
        accessorFn: (row) => coachNameById.get(row.coach_user_id) ?? `#${row.coach_user_id}`,
        id: 'coach',
      },
      {
        header: 'Table',
        accessorFn: (row) => tableNameById.get(row.table_id) ?? `#${row.table_id}`,
        id: 'table',
      },
      {
        header: 'Kind',
        accessorFn: (row) => seriesKindLabel(row.is_one_time),
        id: 'kind',
      },
      {
        // A one-time session is stored as a series of length 1, so it carries a
        // `day_of_week` it doesn't recur on. Rendering "Mon" for a single trial
        // lesson reads as a cadence that doesn't exist — show the date instead.
        header: 'Weekday',
        accessorFn: (row) =>
          row.is_one_time ? row.starts_on : weekdayShortLabel(row.day_of_week),
        id: 'weekday',
      },
      {
        header: 'Time',
        accessorFn: (row) => `${formatClock(row.start_time)}–${formatClock(row.end_time)}`,
        id: 'time',
      },
      {
        header: 'Starts',
        accessorKey: 'starts_on',
        sortingFn: (left, right) => -compareDateDescending(
          { date: left.original.starts_on, id: left.original.id },
          { date: right.original.starts_on, id: right.original.id },
        ),
        sortUndefined: 'last',
      },
      {
        header: 'Ends',
        accessorFn: (row) => row.ends_on ?? '— (open)',
        id: 'ends',
      },
    ],
    [tableNameById, coachNameById],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Coach series</h1>
        <Link to="/coaches/series/new" style={primaryAction}>
          New series
        </Link>
        <Link to="/coaches/one-time/new" style={secondaryAction}>
          New one-time session
        </Link>
      </header>

      <ManageCoachRole onChanged={() => void series.refetch()} />

      <section
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ ...T.subheading, margin: 0 }}>Series</h2>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as CoachSeriesActiveFilter)}
            style={{
              padding: '8px 10px',
              border: `1px solid ${PS.border}`,
              borderRadius: 3,
              minHeight: 40,
              ...T.ui,
              background: PS.white,
            }}
          >
            <option value="true">Active</option>
            <option value="false">Ended</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={() => void series.refetch()}
            style={{
              background: PS.white,
              border: `1px solid ${PS.border}`,
              padding: '8px 12px',
              borderRadius: 3,
              cursor: 'pointer',
              minHeight: 40,
              ...T.navLink,
              textTransform: 'uppercase' as const,
            }}
          >
            Refresh
          </button>
        </header>

        {(series.loading || tables.loading || users.loading) && (
          <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>
        )}

        {series.error && (
          <div
            style={{
              padding: 12,
              background: PS.errorBg,
              border: `1px solid ${PS.errorBorder}`,
              color: PS.errorText,
              borderRadius: 3,
            }}
          >
            {series.error.message}
          </div>
        )}

        {!series.loading && !tables.loading && !users.loading && !series.error && (
          <DataTable
            columns={columns}
            data={orderedSeries}
            emptyLabel="No coach series."
            initialSort={[{ id: 'starts_on', desc: true }]}
            onRowClick={(row) => navigate(`/coaches/series/${row.id}`)}
            getRowLabel={(row) => `Open coach series ${row.id}`}
          />
        )}
      </section>
    </div>
  );
}
